/* Hook sonner's toast → FeedbackOverlay.

   We intercept `toast.success` and `toast.error` (the only two kinds
   we want to be visually loud) and forward them to feedbackBus. The
   raw toast call is intentionally NOT triggered so we don't show both
   the new overlay and the old top-right slide-in.

   `toast.info` / `toast.warning` / `toast.message` / direct `toast()`
   stay as classic sonner toasts — they're used for low-stakes
   informational nudges where the big overlay would feel excessive.

   Import this file ONCE near the application root (see App.js) so the
   patch is applied before any page module fires its first toast.
*/
import { toast } from "sonner";
import feedbackBus from "@/lib/feedbackBus";

const formatMessage = (msg) => {
  if (msg == null) return "";
  if (typeof msg === "string") return msg;
  if (msg instanceof Error) return msg.message || String(msg);
  try { return String(msg); } catch { return ""; }
};

let installed = false;

export function installToastBridge() {
  if (installed || typeof toast !== "function") return;
  installed = true;

  // Preserve the original implementations in case we need to fall back.
  const origSuccess = toast.success?.bind(toast);
  const origError = toast.error?.bind(toast);

  toast.success = (msg /* , opts */) => {
    feedbackBus.show("success", formatMessage(msg));
    // Return a fake toast id so callers that capture/await it don't break.
    return { id: `fb-${Date.now()}` };
  };
  toast.error = (msg /* , opts */) => {
    feedbackBus.show("error", formatMessage(msg));
    return { id: `fb-${Date.now()}` };
  };

  // Expose escape-hatches for any caller that explicitly wants the legacy
  // top-right slide-in (e.g. a non-critical status nudge masquerading as
  // success). Used by /a few/ places that opt out via toast.toastNative.
  toast.toastNative = { success: origSuccess, error: origError };
}
